import { useAuth } from '@core/context/AuthContext';
import { useInventoryState } from '@core/context/InventoryContext';
import { 
    collection, 
    onSnapshot, 
    query, 
    orderBy, 
    addDoc, 
    deleteDoc, 
    doc, 
    updateDoc, 
    serverTimestamp,
    db 
} from '@core/firebase';
import { useState, useEffect, useCallback } from 'react';

/**
 * useCategories - Hook para gestionar el catálogo oficial de categorías.
 */
export const useCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const { products } = useInventoryState();

    useEffect(() => {
        const q = query(collection(db, 'categories'), orderBy('name', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setCategories(snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data() 
            })));
            setLoading(false);
        }, (err) => {
            console.error("Error fetching categories:", err);
            setError(err.message);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const addCategory = useCallback(async (name, description = '') => {
        if (!name?.trim()) throw new Error("Category name is required");
        
        // Evitar duplicados simples localmente
        const exists = categories.some(c => c.name.toLowerCase() === name.trim().toLowerCase());
        if (exists) throw new Error("This category already exists");

        const normalizedName = name.trim();
        await addDoc(collection(db, 'categories'), {
            name: normalizedName,
            description: description.trim(),
            isActive: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_CATEGORY',
            product_name: normalizedName,
            product_description: `New Category: ${normalizedName}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Category ${normalizedName} created`
        });
    }, [categories, user]);

    const updateCategory = useCallback(async (id, newName, newDescription = '') => {
        if (!newName?.trim()) throw new Error("Name is required");
        const categoryRef = doc(db, 'categories', id);
        const normalizedName = newName.trim();
        
        await updateDoc(categoryRef, {
            name: normalizedName,
            description: newDescription.trim(),
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_CATEGORY',
            product_name: normalizedName,
            product_description: `Updated Category: ${normalizedName}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Category metadata updated`
        });
    }, [user]);

    const deleteCategory = useCallback(async (id, options = {}) => {
        const target = categories.find(c => c.id === id);
        if (!target) return;

        const { forceDeleteItems = false, deleteMultipleProductsFn } = options;
        const relatedProducts = products.filter(p => p.category === target.name);

        if (relatedProducts.length > 0) {
            if (!forceDeleteItems) {
                throw new Error(`Cannot delete "${target.name}": This category contains active items.`);
            }
            const productIds = relatedProducts.map(p => p.id);
            await deleteMultipleProductsFn(productIds);
        }

        await deleteDoc(doc(db, 'categories', id));

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_CATEGORY',
            product_name: target.name,
            product_description: `Deleted Category: ${target.name}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Category removed from system`
        });
    }, [categories, products, user]);

    const toggleCategoryStatus = useCallback(async (id, currentStatus) => {
        const target = categories.find(c => c.id === id);
        if (!target) return;

        const newStatus = !currentStatus;
        const categoryRef = doc(db, 'categories', id);
        await updateDoc(categoryRef, {
            isActive: newStatus,
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_CATEGORY',
            product_name: target.name,
            product_description: `${newStatus ? 'Activated' : 'Deactivated'} Category: ${target.name}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Category ${newStatus ? 'activated' : 'deactivated'} in system`
        });
    }, [categories, user]);

    const syncFromProducts = useCallback(async (productCategories) => {
        const unique = [...new Set(productCategories)].filter(Boolean);
        let count = 0;
        for (const name of unique) {
            const exists = categories.some(c => c.name.toLowerCase() === name.toLowerCase());
            if (!exists) {
                await addDoc(collection(db, 'categories'), {
                    name,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                count++;
            }
        }
        return count;
    }, [categories]);

    return { 
        categories, 
        loading, 
        error, 
        addCategory, 
        updateCategory, 
        deleteCategory,
        toggleCategoryStatus,
        syncFromProducts
    };
};
